import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { DEFAULT_COLUMN_WIDTH, MAX_COLUMN_WIDTH, MIN_COLUMN_WIDTH, MIN_SCROLLABLE_REMAINDER } from '../table.constants';
export const emptyColumnsState = ()=>({
        order: [],
        widths: {},
        frozen: [],
        hidden: [],
        labels: {}
    });
const schemaColumnId = (column)=>String(column.name);
export const isMultilineColumn = (column)=>column.type === 'list' || column.type === 'text' && column.multiline === true;
const defaultHAlign = (column)=>column.type === 'money' ? 'end' : 'start';
const schemaColumnHeader = (column)=>column.header;
const DEFAULT_WIDTH = {
    initial: DEFAULT_COLUMN_WIDTH,
    min: MIN_COLUMN_WIDTH,
    max: MAX_COLUMN_WIDTH
};
const clamp = (value, min, max)=>Math.min(Math.max(value, min), max);
const declaredWidth = function declaredWidth() {
    let width = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : DEFAULT_WIDTH;
    return {
        width: width.max == null ? Math.max(width.initial, width.min) : clamp(width.initial, width.min, width.max),
        minWidth: width.min,
        maxWidth: width.max
    };
};
const resolvedWidth = (param)=>{
    let { declared, requested } = param;
    return requested == null ? declared.width : Math.max(declared.minWidth, requested);
};
export function resolveColumn(param) {
    let { column, isMain, columnsState, floors = {} } = param;
    var _floors_id, _columnsState_labels_id, _column_hAlign;
    const id = schemaColumnId(column);
    const base = declaredWidth(column.width);
    // WHY: the floor raises the minimum and the resting width, and the ceiling makes room for it.
    const minWidth = Math.max(base.minWidth, (_floors_id = floors[id]) !== null && _floors_id !== void 0 ? _floors_id : 0);
    const declared = {
        width: Math.max(base.width, minWidth),
        minWidth,
        maxWidth: base.maxWidth == null ? undefined : Math.max(base.maxWidth, minWidth)
    };
    const width = resolvedWidth({
        declared,
        requested: columnsState.widths[id]
    });
    return {
        id,
        isMain,
        label: (_columnsState_labels_id = columnsState.labels[id]) !== null && _columnsState_labels_id !== void 0 ? _columnsState_labels_id : schemaColumnHeader(column),
        width,
        minWidth: declared.minWidth,
        maxWidth: declared.maxWidth == null ? undefined : Math.max(declared.maxWidth, width),
        resized: columnsState.widths[id] != null,
        frozen: isMain || columnsState.frozen.includes(id),
        hAlign: (_column_hAlign = column.hAlign) !== null && _column_hAlign !== void 0 ? _column_hAlign : defaultHAlign(column),
        column
    };
}
export function resolveColumns(param) {
    let { mainColumn, columns, columnsState, floors } = param;
    const byId = new Map();
    columns.forEach((column)=>{
        const resolved = resolveColumn({
            column,
            isMain: false,
            columnsState,
            floors
        });
        byId.set(resolved.id, resolved);
    });
    const resolvedMainColumn = mainColumn == null ? [] : [
        resolveColumn({
            column: mainColumn,
            isMain: true,
            columnsState,
            floors
        })
    ];
    const ordered = columnsState.order.flatMap((id)=>{
        const resolved = byId.get(id);
        return resolved ? [
            resolved
        ] : [];
    });
    const orderedIds = new Set(ordered.map((c)=>c.id));
    const remaining = [
        ...byId.values()
    ].filter((c)=>!orderedIds.has(c.id));
    return [
        ...resolvedMainColumn,
        ...[
            ...ordered,
            ...remaining
        ].filter((c)=>!columnsState.hidden.includes(c.id))
    ];
}
export function frozenPrefixLength(resolved) {
    const firstUnfrozen = resolved.findIndex((c)=>!c.frozen);
    return firstUnfrozen === -1 ? resolved.length : firstUnfrozen;
}
export function affordableFrozenPrefix(param) {
    let { resolved, checkboxWidth, containerWidth } = param;
    const prefixLength = frozenPrefixLength(resolved);
    if (containerWidth <= 0) return prefixLength;
    const blockWidth = resolved.slice(0, prefixLength).reduce((total, column)=>total + column.width, checkboxWidth);
    return blockWidth + MIN_SCROLLABLE_REMAINDER <= containerWidth ? prefixLength : 0;
}
export function frozenOffsets(param) {
    let { resolved, checkboxWidth } = param;
    const prefixLength = frozenPrefixLength(resolved);
    const offsets = {};
    let accumulated = checkboxWidth;
    resolved.slice(0, prefixLength).forEach((column)=>{
        offsets[column.id] = accumulated;
        accumulated += column.width;
    });
    return offsets;
}
const boundedWidth = (column, share)=>{
    var _column_minWidth, _column_maxWidth;
    return Math.min(Math.max(share, (_column_minWidth = column.minWidth) !== null && _column_minWidth !== void 0 ? _column_minWidth : 0), (_column_maxWidth = column.maxWidth) !== null && _column_maxWidth !== void 0 ? _column_maxWidth : Number.POSITIVE_INFINITY);
};
export function stretchColumns(param) {
    let { resolved, availableWidth } = param;
    if (availableWidth <= 0) return [
        ...resolved
    ];
    const declaredTotal = resolved.reduce((total, column)=>total + column.width, 0);
    if (declaredTotal > availableWidth) return shrinkColumns({
        resolved,
        availableWidth
    });
    if (declaredTotal === availableWidth) return [
        ...resolved
    ];
    // WHY: after a resize only the last column flexes; its dragged width is a floor, so it fills the room or overflows.
    if (resolved.some((column)=>column.resized === true)) {
        const flexed = resolved.map((column)=>_object_spread({}, column));
        const last = flexed[flexed.length - 1];
        if (last != null) {
            last.width = boundedWidth(last, last.width + (availableWidth - declaredTotal));
        }
        return flexed;
    }
    // WHY: proportional sharing keeps a narrow column narrow while the table still fills its host.
    const stretched = resolved.map((column)=>_object_spread({}, column));
    let leftover = availableWidth - declaredTotal;
    const canGrow = (column)=>boundedWidth(column, column.width + 1) > column.width;
    let growable = stretched.filter(canGrow);
    while(leftover > 0 && growable.length > 0){
        const growableTotal = growable.reduce((total, column)=>total + column.width, 0);
        const before = leftover;
        growable.forEach((column)=>{
            const share = Math.floor(before * column.width / growableTotal);
            const grown = boundedWidth(column, column.width + share);
            leftover -= grown - column.width;
            column.width = grown;
        });
        growable = growable.filter(canGrow);
        if (leftover === before) break;
    }
    const last = growable[growable.length - 1];
    if (last != null && leftover > 0) last.width = boundedWidth(last, last.width + leftover);
    return stretched;
}
export function shrinkColumns(param) {
    let { resolved, availableWidth } = param;
    const shrunk = resolved.map((column)=>_object_spread({}, column));
    const shrinkable = shrunk.filter((column)=>{
        var _column_minWidth;
        return column.resized !== true && column.width > ((_column_minWidth = column.minWidth) !== null && _column_minWidth !== void 0 ? _column_minWidth : 0);
    });
    if (shrinkable.length === 0) return shrunk;
    const slack = shrinkable.reduce((total, column)=>{
        var _column_minWidth;
        return total + (column.width - ((_column_minWidth = column.minWidth) !== null && _column_minWidth !== void 0 ? _column_minWidth : 0));
    }, 0);
    const deficit = shrunk.reduce((total, column)=>total + column.width, 0) - availableWidth;
    if (deficit <= 0) return shrunk;
    if (deficit >= slack) {
        shrinkable.forEach((column)=>{
            var _column_minWidth;
            column.width = (_column_minWidth = column.minWidth) !== null && _column_minWidth !== void 0 ? _column_minWidth : 0;
        });
        return shrunk;
    }
    let remaining = deficit;
    shrinkable.forEach((column, index)=>{
        var _column_minWidth;
        const columnSlack = column.width - ((_column_minWidth = column.minWidth) !== null && _column_minWidth !== void 0 ? _column_minWidth : 0);
        const proportional = index === shrinkable.length - 1 ? remaining : Math.round(deficit * (columnSlack / slack));
        const applied = Math.min(proportional, remaining, columnSlack);
        column.width -= applied;
        remaining -= applied;
    });
    return shrunk;
}
export function minTableWidth(param) {
    let { resolved, checkboxWidth } = param;
    const columnsWidth = resolved.reduce((total, column)=>total + column.width, 0);
    return checkboxWidth + columnsWidth;
}
export function applyResize(param) {
    let { state, columnId, width, minWidth = MIN_COLUMN_WIDTH } = param;
    // WHY: a ceiling would snap a stretched column back the moment a drag begins.
    return _object_spread_props(_object_spread({}, state), {
        widths: _object_spread_props(_object_spread({}, state.widths), {
            [columnId]: Math.max(minWidth, Math.round(width))
        })
    });
}
export function applyToggleFrozen(param) {
    let { state, resolved, columnId } = param;
    const target = resolved.find((column)=>column.id === columnId);
    if (!target) return state;
    const wasFrozen = state.frozen.includes(columnId);
    const frozen = wasFrozen ? state.frozen.filter((id)=>id !== columnId) : [
        ...state.frozen,
        columnId
    ];
    const order = resolved.map((column)=>column.id).filter((id)=>id !== columnId);
    const lastFrozenIndex = order.reduce((last, id, index)=>frozen.includes(id) ? index : last, -1);
    const insertAt = wasFrozen ? resolved.findIndex((column)=>column.id === columnId) : lastFrozenIndex + 1;
    order.splice(Math.max(0, Math.min(order.length, insertAt)), 0, columnId);
    return _object_spread_props(_object_spread({}, state), {
        frozen,
        order
    });
}
export function applyDroppedOrder(param) {
    let { state, order } = param;
    // WHY: frozen is a leading run, so the first column that was not frozen ends it - dropping an
    // unfrozen column into the middle unfreezes it and everything to its right.
    const frozen = [];
    for (const id of order){
        if (!state.frozen.includes(id)) break;
        frozen.push(id);
    }
    return _object_spread_props(_object_spread({}, state), {
        order: [
            ...order
        ],
        frozen
    });
}
